import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { jobsApi } from '../services/api';
import { format } from 'date-fns';
import { ArrowLeft, FileText, Rows3, Clock, Download, Database, Loader, Building2} from 'lucide-react';
import { sftpApi } from '../services/api';
import toast from 'react-hot-toast';

function NoTenantSelected() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center' }}>
      <Building2 size={40} style={{ color: 'var(--text-dim)', marginBottom: 16 }} />
      <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>No Tenant Selected</h3>
      <p style={{ fontSize: 13, color: 'var(--text-dim)', maxWidth: 320, lineHeight: 1.6 }}>
        Use the tenant switcher in the sidebar to select a tenant and view their data.
      </p>
    </div>
  );
}

function Badge({ status }) {
  const map = {
    success: 'badge-success', failed: 'badge-failed',
    partial: 'badge-partial', running: 'badge-running', pending: 'badge-pending',
  };
  return (
    <span className={`badge ${map[status] || 'badge-pending'}`}>
      <span className="badge-dot" />
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}


export default function JobDetail() {
  const { user, activeTenantId } = useAuth();
  if (user?.is_admin && !activeTenantId) return <NoTenantSelected />;

  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const logEndRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [downloading, setDownloading] = useState({});

  const fetchJob = async () => {
    try {
      const { data } = await jobsApi.get(id);
      setJob(data);
      if (['pending', 'running'].includes(data.status)) {
        setTimeout(fetchJob, 2000);  // poll while running
      }
    } catch (err) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = useCallback(async () => {
    try {
      const { data } = await sftpApi.listFiles(100);
      setFiles(data.filter(f => f.job_id === id));
    } catch { /* SFTP may not be configured */ }
  }, [id]);

  useEffect(() => { fetchJob(); fetchFiles(); }, [id]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [job?.log_entries?.length]);

  function formatBytes(bytes) {
    if (!bytes) return '0 B';
    const k = 1024, sizes = ['B','KB','MB','GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }

  const handleDownload = async (file) => {
    setDownloading(d => ({ ...d, [file.id]: true }));
    try {
      let url = file.download_url;
      if (!url) { const res = await sftpApi.getDownloadUrl(file.id); url = res.data.download_url; }
      window.open(url, '_blank');
    } catch { toast.error('Failed to get download link'); }
    finally { setDownloading(d => ({ ...d, [file.id]: false })); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!job) return <div className="empty-state"><h3>Job not found</h3></div>;

  const start = new Date(job.started_at);
  const end = job.finished_at ? new Date(job.finished_at) : null;
  const duration = end ? `${Math.round((end - start) / 1000)}s` : '…';

  return (
    <div className="fade-in">
      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/jobs')} style={{ marginBottom: 16 }}>
        <ArrowLeft size={14} /> Back to History
      </button>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Sync Job</h1>
          <p className="page-subtitle" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{job.id}</p>
        </div>
        <Badge status={job.status} />
      </div>

      {/* Meta */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { icon: Clock, label: 'Started', value: format(start, 'MMM d, h:mm:ss a') },
          { icon: Clock, label: 'Duration', value: duration },
          { icon: FileText, label: 'Files Translated', value: `${job.files_translated} / ${job.files_found}` },
          { icon: Rows3, label: 'Rows Processed', value: job.rows_processed.toLocaleString() },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="card" style={{ padding: 16 }}>
            <div className="stat-label">{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{value}</div>
          </div>
        ))}
      </div>

      {job.error_message && (
        <div className="alert-box alert-error" style={{ marginBottom: 16 }}>
          <span style={{ fontWeight: 600 }}>Error:</span> {job.error_message}
        </div>
      )}

      {/* Log */}
      <div className="card">
        <div className="card-title">
          Execution Log
          {['pending', 'running'].includes(job.status) && (
            <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--blue)' }}>● Live</span>
          )}
        </div>
        <div className="log-list" style={{ maxHeight: 480, overflowY: 'auto' }}>
          {job.log_entries.length === 0 ? (
            <div style={{ color: 'var(--text-dim)', padding: '20px 0', textAlign: 'center' }}>
              No log entries yet…
            </div>
          ) : (
            job.log_entries.map(entry => (
              <div key={entry.id} className={`log-entry log-${entry.level}`}>
                <div className="log-time">{format(new Date(entry.created_at), 'HH:mm:ss')}</div>
                <div className="log-msg">{entry.message}</div>
                {entry.rows != null && (
                  <div className="log-rows">{entry.rows.toLocaleString()} rows</div>
                )}
              </div>
            ))
          )}
          <div ref={logEndRef} />
        </div>
      </div>
      {/* Translated files */}
      {files.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title">Translated Files</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {files.map(file => (
              <div key={file.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', background: 'var(--navy)',
                border: '1px solid var(--navy-border)', borderRadius: 8, minWidth: 200,
              }}>
                <FileText size={15} style={{ color: 'var(--blue)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{file.file_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2, display: 'flex', gap: 8 }}>
                    <span><Database size={10} style={{ display: 'inline', marginRight: 3 }} />{file.rows?.toLocaleString() ?? '—'} rows</span>
                    <span>{formatBytes(file.size_bytes)}</span>
                  </div>
                </div>
                <button onClick={() => handleDownload(file)} disabled={downloading[file.id]}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', padding: 4 }}>
                  {downloading[file.id] ? <Loader size={14} className="spin" /> : <Download size={14} />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
